import { useState, useMemo, useEffect, useRef, CSSProperties, Fragment, ReactNode } from "react"
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  PaginationState,
  ExpandedState,
  getExpandedRowModel,
  Column,
} from "@tanstack/react-table"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"
import { DictType } from "@pma/types"
import Loader from "@components/common/loader"
import AddTaskInput from "@components/projects/add-task-input"
import useTranslator from "@hooks/use-translator"
import { useLocation, useParams, useSearchParams } from "react-router-dom"
import { projectFilterFn, taskFilterFn } from "@pma/utils/helpers"
import { Pin, PinOff } from "lucide-react"
import { useIntersectionObserver } from "@pma/hooks/use-intersection-oberserver"
import { DataTableViewOptions } from "./data-table-view-options"
import { Button } from "@components/common/button"
import { useQuery } from "@tanstack/react-query"
import TableBatchActions from "@pma/components/projects/table-batch-actions"
import { DataTablePagination } from "./data-table-pagination"
import { useAuth } from "@pma/stores/auth-store"
import InfiniteScroll from "react-infinite-scroll-component"

import TBody from "./table-body"

interface DataTableProps<AnyType> {
  ref?: any
  isLoading: boolean
  columns: ColumnDef<any>[]
  data: Array<DictType>
  refetch?: CallableFunction
  context?: "tasks" | "projects" | "users"
  searchKey?: string
  toolbar?: ReactNode
  paginationData?: any
  setPagination?: any
  className?: string
  enableInfiniteScroll?: boolean
  fetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  onRowClick?: (row: DictType) => void
  showMessage?: string
  translate?: (key: string) => string
  iszIndexApply?: boolean
  scrollableTarget?: string
}

export function DataTable<AnyType>({
  className,
  setPagination,
  paginationData,
  isLoading,
  columns,
  data,
  ref,
  context,
  searchKey,
  toolbar,
  enableInfiniteScroll = false,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onRowClick,
  showMessage,
  iszIndexApply = false,
  scrollableTarget,
}: DataTableProps<any>) {
  const [rowSelection, setRowSelection] = useState({})
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [sorting, setSorting] = useState<SortingState>([])
  const [selectedTasks, setSelectedTasks] = useState<
    Array<{
      taskId: number
      priorityId: number
      responsible_id: number
      project_id: number
      group_id: number
    }>
  >([])

  const [expanded, setExpanded] = useState<ExpandedState>({})
  const [showLoader, setShowLoader] = useState(false)
  const [expandedRow, setExpandedRow] = useState<null | number>(null)

  const tableRef = useRef<any>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  const { authConfig } = useAuth()

  const isBottomVisible = useIntersectionObserver(bottomRef, { threshold: 0 })

  let globalFilterFn

  switch (context) {
    case "tasks":
      globalFilterFn = taskFilterFn
      break
    case "projects":
      globalFilterFn = projectFilterFn
      break
    default:
      globalFilterFn = undefined
      break
  }

  useEffect(() => {
    setShowLoader(isLoading)
  }, [isLoading, data, paginationData])

  useEffect(() => {
    let wrapper2 = document.getElementById("wrapper2")
    const tableRow = table.getRowModel().rows

    if (wrapper2 && tableRow.length < 5) {
      wrapper2.classList.remove("sticky-style")
    } else if (wrapper2 && tableRow.length > 5) {
      wrapper2.classList.add("sticky-style")
    }
  }, [searchKey, data])

  const { translate, locale } = useTranslator()
  const paramProjectId = useParams()?.projectId
  const [searchParam, SetURLSearchParams] = useSearchParams()
  const loggedInUser = localStorage.getItem("rog_user")
    ? JSON.parse(localStorage?.getItem("rog_user") || "")
    : ""
  const paramTaskId = searchParam.get("task")
  const queryParamId = searchParam.get("query")

  const setHeaderPins = (pinArr: string[]) => {
    const headerGroup = table.getHeaderGroups()
    const headers = headerGroup[0]?.headers || []

    if (headers && headers.length > 0) {
      headers.forEach((head) => {
        if (pinArr.includes(head.id)) {
          head.column.pin("left")
        }
      })
    }
  }

  useEffect(() => {
    const VisibilityArr = localStorage.getItem("field-visibility")
      ? JSON.parse(localStorage.getItem("field-visibility") || "")
      : ""
    if (!VisibilityArr) {
      const arr = []
      arr.push({
        field: columnVisibility,
        projectId: paramProjectId,
        user: loggedInUser.email,
      })
      localStorage.setItem("field-visibility", JSON.stringify(arr))
    } else if (VisibilityArr.length > 0) {
      const found = VisibilityArr.find(
        (val: any) => val.projectId === paramProjectId && val.user === loggedInUser.email
      )
      const index = VisibilityArr.indexOf(found)

      if (found) {
        if (Object.values(columnVisibility).length > 0) {
          found["field"] = columnVisibility
          VisibilityArr[index] = found
          localStorage.setItem("field-visibility", JSON.stringify(VisibilityArr))
        } else {
          setColumnVisibility(found["field"])
        }
      } else {
        VisibilityArr.push({
          field: columnVisibility,
          projectId: paramProjectId,
          user: loggedInUser.email,
        })
        localStorage.setItem("field-visibility", JSON.stringify(VisibilityArr))
      }
    }
  }, [columnVisibility])

  useEffect(() => {
    const pinnedArr = localStorage.getItem("pinned")
      ? JSON.parse(localStorage?.getItem("pinned") || "")
      : []
    if (pinnedArr) {
      const currentProject = pinnedArr.find((val: any) => val.projectId === paramProjectId)

      if (currentProject && currentProject?.user === loggedInUser.email) {
        setHeaderPins(currentProject.pinned)
      }
    } else {
      localStorage.setItem("pinned", JSON.stringify([]))
    }

    let wrapper2 = document.getElementById("wrapper2")

    const tableRow = table.getRowModel().rows

    if (wrapper2 && className && tableRow.length > 0) {
      wrapper2.classList.add("sticky-style")
    }
  }, [])

  useEffect(() => {
    if (queryParamId) {
      setExpandedRow(Number(queryParamId))
    } else if (expandedRow) {
      const tableRow = table.getRowModel().rows.find((r) => r.original.id === expandedRow)

      if (tableRow && tableRow.getCanExpand() && tableRow.getIsExpanded()) {
        tableRow.getToggleExpandedHandler()()
        setExpandedRow(null)
      }
    }
  }, [queryParamId])

  const table = useReactTable({
    data: data || [],
    columns,
    // pageCount: Math.ceil(data?.length / pageSize) ?? 1,
    state: {
      sorting,
      // pagination,
      rowSelection,
      columnFilters,
      columnVisibility,
      expanded,
      globalFilter: searchKey,
    },
    onExpandedChange: setExpanded,
    // onPaginationChange: setPagination,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),

    manualPagination: false,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    debugTable: false,
    getRowCanExpand: (row) => !row.original?.parent_tasks_id,
    getSubRows: (row) => row._subtasks,
    globalFilterFn: globalFilterFn,
  })

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const projectId = context === "projects" ? data[0]?.project_id || paramProjectId : undefined

  useEffect(() => {
    const { flatRows } = table.getGroupedSelectedRowModel()

    const arr: Array<{
      taskId: number
      priorityId: number
      responsible_id: number
      project_id: number
      group_id: number
    }> = flatRows.map((val) => {
      return {
        taskId: val.original.id,
        priorityId: val.original.priority_id,
        responsible_id: val.original.responsible_id,
        project_id: val.original.project_id,
        group_id: val.original.tasks_group_id,
      }
    })
    setSelectedTasks(arr)
  }, [table.getGroupedSelectedRowModel()])

  useQuery({
    queryKey: ["show-loader"],
    queryFn: () => {
      setShowLoader(true)
      table.resetRowSelection()
      return false
    },
  })

  useQuery({
    queryKey: ["clear-selected-tasks"],
    queryFn: () => {
      setShowLoader(true)
      table.resetRowSelection()

      setTimeout(() => {
        setShowLoader(false)
      }, 800)

      return false
    },
  })

  useEffect(() => {
    // Trigger scroll when data is available in the table
    if (data?.length > 0) {
      setTimeout(() => {
        if (paramTaskId && !showLoader && tableRef?.current) {
          // Find the index of the task by ID
          const taskIndex = data.findIndex((task: DictType) => {
            return task.id === Number(paramTaskId)
          })

          // Scroll to the task and add focus class
          if (taskIndex !== -1 && tableRef?.current) {
            const row = tableRef?.current?.querySelector(`[data-task-id='${paramTaskId}']`)
            if (row) {
              row.scrollIntoView({ behavior: "smooth", block: "center" })
              row.classList.add("focus-row")

              const tableRow = table
                .getRowModel()
                .rows.find((r) => r.original.id === Number(paramTaskId))

              // Check if the row can expand and trigger the expansion
              if (tableRow && tableRow.getCanExpand() && !tableRow.getIsExpanded()) {
                tableRow.getToggleExpandedHandler()()
              }
            }
          }
        }
      }, 1000)
    }
  }, [paramTaskId, data?.length, searchParam])

  const getCommonPinningStyles = (column: Column<DictType>): CSSProperties => {
    const isPinned = column.getIsPinned()
    return {
      boxShadow: isPinned ? "-4px 0 4px -4px gray inset" : undefined,
      left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
      opacity: isPinned ? 0.95 : 1,
      position: isPinned ? "sticky" : "relative",
      width: column.getSize(),
      zIndex: isPinned ? 1 : 0,
      background: isPinned ? "white" : undefined,
    }
  }

  const columnSizingHandler = (
    thElem: HTMLTableCellElement | null,
    table: any,
    column: Column<any>
  ) => {
    if (!thElem) return
    const currentWidth = thElem.getBoundingClientRect().width
    if (table.getState().columnSizing[column.id] === currentWidth) return

    table.setColumnSizing((prevSizes: any) => ({
      ...prevSizes,
      [column.id]: currentWidth,
    }))
  }

  const handleHeaderPin = (pin: any, pinStatus: boolean | string = false) => {
    const pinnedArr = localStorage.getItem("pinned")
      ? JSON.parse(localStorage?.getItem("pinned") || "")
      : []

    if (pinnedArr && pinnedArr.length > 0) {
      const currentProject = pinnedArr.find(
        (val: any) => val.projectId === paramProjectId && val.user === loggedInUser.email
      )
      if (currentProject) {
        if (pinStatus === false) {
          const index = pinnedArr.indexOf(currentProject)
          const pinArr = currentProject.pinned.filter((val: string) => val !== pin.id)
          currentProject["pinned"] = pinArr
          pinnedArr[index] = currentProject

          pin.column.pin(false)
          localStorage.setItem("pinned", JSON.stringify(pinnedArr))
        } else {
          const index = pinnedArr.indexOf(currentProject)
          const pinArr = currentProject.pinned
          pinArr.push(pin.id)
          currentProject["pinned"] = pinArr
          pinnedArr[index] = currentProject

          pin.column.pin(pinStatus)
          localStorage.setItem("pinned", JSON.stringify(pinnedArr))
        }
      } else {
        if (pinStatus === "left") {
          const pinArr = []
          pinArr.push(pin.id)
          pinnedArr.push({
            projectId: paramProjectId,
            pinned: pinArr,
            user: loggedInUser.email,
          })

          pin.column.pin(pinStatus)
          localStorage.setItem("pinned", JSON.stringify(pinnedArr))
        }
      }
    } else {
      if (pinStatus === false) {
        pin.column.pin(false)
      } else {
        const pinArr = []
        pinArr.push(pin.id)
        pinnedArr.push({
          projectId: paramProjectId,
          pinned: pinArr,
          user: loggedInUser.email,
        })

        pin.column.pin(pinStatus)
        localStorage.setItem("pinned", JSON.stringify(pinnedArr))
      }
    }
  }

  const clearCurrentUserTableFormats = () => {
    const pinnedArr = localStorage.getItem("pinned")
      ? JSON.parse(localStorage?.getItem("pinned") || "")
      : []

    const VisibilityArr = localStorage.getItem("field-visibility")
      ? JSON.parse(localStorage.getItem("field-visibility") || "")
      : []

    const arr = localStorage?.getItem("task-table-order")
      ? JSON.parse(localStorage?.getItem("task-table-order") || "")
      : []

    if (pinnedArr && pinnedArr.length > 0) {
      const found = pinnedArr.find(
        (val: any) => val.projectId === paramProjectId && val.user === loggedInUser.email
      )
      if (found) {
        const index = pinnedArr.indexOf(found)
        found["pinned"] = []
        pinnedArr[index] = found
        localStorage.setItem("pinned", JSON.stringify(pinnedArr))
      }
    }
    if (VisibilityArr && pinnedArr.length > 0) {
      const found = VisibilityArr.find(
        (val: any) => val.projectId === paramProjectId && val.user === loggedInUser.email
      )
      if (found) {
        const index = VisibilityArr.indexOf(found)
        found["field"] = []
        VisibilityArr[index] = found
        localStorage.setItem("field-visibility", JSON.stringify(VisibilityArr))
      }
    }
    if (arr && arr.length > 0) {
      const found = arr.find(
        (val: any) => val.projectId === paramProjectId && val.user === loggedInUser.email
      )
      if (found) {
        const index = arr.indexOf(found)
        arr[index]["order_key"] = ""
        arr[index]["order_value"] = ""
        localStorage.setItem("task-table-order", JSON.stringify(arr))
      }
    }
    window.location.reload()
  }

  useEffect(() => {
    if (expanded) {
      SetURLSearchParams((prev) => {
        prev.delete("task")
        return prev
      })
    }
  }, [expanded, paramTaskId])

  // Handle next page fetch for infinite scroll
  const handleFetchNextPage = () => {
    if (hasNextPage && !isFetchingNextPage && fetchNextPage) {
      fetchNextPage()
    }
  }

  return (
    <div className="relative space-y-4">
      <div className="flex gap-2">
        <div className="flex-1">{toolbar}</div>

        <DataTableViewOptions
          table={table}
          projectId={paramProjectId}
          context={context ?? ""}
          columns={columns}
        />

        {paramProjectId && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto hidden h-8 lg:flex"
            onClick={clearCurrentUserTableFormats}
          >
            {translate("Clear Formats")}
          </Button>
        )}
      </div>

      <div className="rounded-md border">
        {enableInfiniteScroll ? (
          <InfiniteScroll
            dataLength={data.length}
            next={handleFetchNextPage}
            hasMore={hasNextPage || false}
            loader={
              isFetchingNextPage ? (
                <div className="flex items-center justify-center py-4">
                  <Loader />
                </div>
              ) : null
            }
            scrollableTarget={scrollableTarget}
            scrollThreshold={0.8}
            endMessage={
              data.length > 0 ? (
                <div className="py-4 text-center text-sm text-muted-foreground">
                  {translate(showMessage ? showMessage : "No more items to load")}
                </div>
              ) : null
            }
          >
            <Table ref={tableRef}>
              <TableHeader id="myHeader">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className={`${className ? "sticky top-0 z-[50] bg-white/30 backdrop-blur-md" : ""} `}
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        style={{ ...getCommonPinningStyles(header.column) }}
                        ref={(thElem) => columnSizingHandler(thElem, table, header.column)}
                      >
                        <div className="flex">
                          <span className="">
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          <div className="flex w-full flex-1 items-center">
                            {header.column.getIsPinned() ? (
                              <PinOff
                                onClick={() => handleHeaderPin(header)}
                                className="m-1 h-3.5 w-3.5 text-muted-foreground/70"
                              />
                            ) : (
                              <Pin
                                onClick={() => handleHeaderPin(header, "left")}
                                className="m-1 h-3.5 w-3.5 text-muted-foreground/70"
                              />
                            )}
                          </div>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TBody
                table={table}
                tableContainerRef={tableContainerRef}
                showLoader={showLoader}
                projectId={projectId}
                columns={columns}
                getCommonPinningStyles={getCommonPinningStyles}
                context={context}
                onRowClick={onRowClick}
                dataLength={data.length}
                iszIndexApply={iszIndexApply}
              />
            </Table>
          </InfiniteScroll>
        ) : (
          <Table ref={tableRef}>
            <TableHeader id="myHeader">
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <TableRow
                    key={headerGroup.id}
                    className={`${className ? "sticky top-0 z-[50] bg-white/30 backdrop-blur-md" : ""} `}
                  >
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead
                          key={header.id}
                          style={{ ...getCommonPinningStyles(header.column) }}
                          ref={(thElem) => columnSizingHandler(thElem, table, header.column)}
                        >
                          <div className="flex">
                            <span className="">
                              {header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                            <div className="flex w-full flex-1 items-center">
                              {header.column.getIsPinned() ? (
                                <PinOff
                                  onClick={() => {
                                    handleHeaderPin(header)
                                  }}
                                  className="m-1 h-3.5 w-3.5 text-muted-foreground/70"
                                />
                              ) : (
                                <Pin
                                  onClick={() => handleHeaderPin(header, "left")}
                                  className="m-1 h-3.5 w-3.5 text-muted-foreground/70"
                                />
                              )}
                            </div>
                          </div>
                        </TableHead>
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableHeader>
            <TBody
              table={table}
              tableContainerRef={tableContainerRef}
              showLoader={showLoader}
              projectId={projectId}
              columns={columns}
              getCommonPinningStyles={getCommonPinningStyles}
              context={context}
              onRowClick={onRowClick}
              dataLength={data.length}
              iszIndexApply={iszIndexApply}
            />
          </Table>
        )}
        <div ref={bottomRef} className="h-1" />
        {!enableInfiniteScroll && paginationData && (
          <DataTablePagination
            data={data}
            paginationData={paginationData}
            setPagination={setPagination}
          />
        )}
      </div>

      {selectedTasks.length > 0 && (
        <TableBatchActions tasks={selectedTasks} onCancel={() => table.resetRowSelection()} />
      )}

      {!isBottomVisible && context === "tasks" && (
        <div className="fixed left-0 right-0 top-[84vh] flex h-28 items-center justify-center bg-transparent p-4 shadow-md transition-all duration-300 ease-in-out">
          <AddTaskInput projectId={data[0]?.project_id} />
        </div>
      )}
    </div>
  )
}